import contrastRatio from './contrastRatio'
// Het vaste Deccos-palet voor grafieken.
//
// Waarom vast: tot nu toe kwamen grafiekkleuren uit de huisstijl van de
// organisatie (drie kleuren) plus een kleurkiezer per kaart. Drie kleuren zijn
// er te weinig — bij zes matrixrijen kreeg rij 4 dezelfde kleur als rij 1,
// zonder enige waarschuwing. En omdat elke organisatie eigen kleuren koos, zag
// geen twee dashboards er hetzelfde uit.
//
// Kleursoort volgt datasoort. Dat is de hele regel:
//   categorisch  — antwoordopties, matrixrijen, categorieën   -> CATEGORICAL
//   ordinaal     — Likert 1-5, participatieladder, schaal     -> SEQUENTIAL
//   divergerend  — negatief geformuleerde rijen, groei +/-    -> DIVERGING
//   vaste betekenis — resultaat/doel/benchmark                -> scoreColors.js
//
// Een ordinale reeks door CATEGORICAL halen is de meest gemaakte fout: dan
// suggereren de kleuren verschil waar volgorde bedoeld is.
//
// Live rapportages gebruiken dit palet NIET — daar houdt de organisatie haar
// eigen huisstijl. Die splitsing loopt via resolveChartPalette in
// visualisationDefaults.js, op basis van LiveReportRenderContext.
//
// AI-CONTEXT: See docs/features/chart-color-system.md

/**
 * Categorisch palet: acht kleuren voor data zonder volgorde.
 *
 * De eerste drie zijn letterlijk de merkkleuren van deccos.nl. Vier tot en met
 * acht zijn erbij gerekend, want met drie kleurtonen krijg je geen acht
 * onderscheidbare series. De volgorde is bewust: elke serie verschilt zo veel
 * mogelijk van zijn buur én van alle andere.
 *
 * Nagerekend in oklab, ook door de bril van drie vormen van kleurenblindheid —
 * zie chartPalette.test.js. Zet je hier een kleur bij of om, dan meet die test
 * of het palet nog werkt.
 *
 * @type {string[]}
 */
export const CATEGORICAL = [
    '#47acc3', // teal (merk)
    '#f48183', // koraal (merk)
    '#206c7f', // donker teal (merk, de kleur van het logo)
    '#b68028', // oker
    '#6f5994', // paars
    '#407135', // groen
    '#825652', // roodbruin
    '#8c96a1', // leisteen
]

/**
 * Ordinaal palet: één kleurfamilie van licht naar donker, voor data mét
 * volgorde. De middelste stap is de merk-teal, zodat een reeks als Deccos leest
 * en niet als een willekeurige blauwe ramp.
 *
 * @type {string[]}
 */
export const SEQUENTIAL = ['#d4edf4', '#a2d4e1', '#47acc3', '#298b9f', '#186575']

/**
 * Divergerend palet: koraal (negatief) via neutraal naar teal (positief). Voor
 * waarden die om een middelpunt draaien — een negatief geformuleerde matrixrij,
 * of groei die zowel omhoog als omlaag kan.
 *
 * @type {string[]}
 */
export const DIVERGING = ['#bb6263', '#d79d9c', '#eceef0', '#86b7c4', '#29889c']

/**
 * Randje om gevulde vlakken (staven, taartpunten).
 *
 * De twee merkkleuren zijn licht: teal haalt 2,64:1 op wit en koraal 2,53:1,
 * onder de 3:1 die voor gekleurde vlakken geldt. We verdonkeren ze niet — dan
 * staat er in de grafiek een andere teal dan op de knop ernaast. In plaats
 * daarvan krijgt elk gevuld vlak een dunne donkere rand, zodat de vorm ook
 * zichtbaar blijft als de kleur dat niet is.
 *
 * @type {string}
 */
export const CHART_MARK_STROKE = 'rgba(0, 0, 0, 0.18)'

/**
 * Kleur voor de verzamelbak 'Overig'.
 *
 * Boven acht series begint CATEGORICAL opnieuw, dus dan hoort een grafiek te
 * groeperen in plaats van kleuren te hergebruiken. Die groep is geen categorie
 * en krijgt daarom bewust geen kleur uit het palet: een rustig neutraal, zodat
 * hij niet meepraat met de series die er wél toe doen.
 *
 * @type {string}
 */
export const OTHER_BUCKET_COLOR = '#c3ccd2'

/** @type {number} Breedte die bij CHART_MARK_STROKE hoort. */
export const CHART_MARK_STROKE_WIDTH = 0.5

/**
 * De kleur voor serie `index`. Boven acht begint het palet opnieuw — een
 * bewuste keuze, geen ongeluk. Heeft een grafiek meer dan acht series, dan hoort
 * de afnemer te groeperen in één 'Overig', zoals MultipleData doet.
 *
 * @param {number} index - 0-gebaseerde positie van de serie
 * @returns {string} Hexkleur
 */
export const getSeriesColor = (index) => {
    if (!Number.isFinite(index) || index < 0) return CATEGORICAL[0]
    return CATEGORICAL[Math.floor(index) % CATEGORICAL.length]
}

/** @param {string} hex @returns {number[]} sRGB-kanalen 0-1 */
const hex2rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)

/** @param {number[]} rgb @returns {string} */
const rgb2hex = (rgb) =>
    '#' + rgb.map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, '0')).join('')

/**
 * Mengt twee kleuren in lineair licht in plaats van in hexwaarden. Rechtstreeks
 * tussen hexcodes middelen geeft een doffe tussenkleur, omdat sRGB niet lineair
 * is in helderheid.
 *
 * @param {string} a @param {string} b @param {number} t - 0 = a, 1 = b
 * @returns {string}
 */
const meng = (a, b, t) => {
    const lin = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
    const unlin = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)
    const A = hex2rgb(a).map(lin)
    const B = hex2rgb(b).map(lin)
    return rgb2hex(A.map((v, i) => unlin(v + (B[i] - v) * t)))
}

/**
 * Verdeelt `count` kleuren evenredig over een ramp, van de eerste tot en met de
 * laatste stap. Bij minder kleuren dan stappen wordt er overgeslagen (drie
 * treden worden licht/midden/donker, niet de eerste drie tinten — anders lijken
 * drie opeenvolgende niveaus alle drie hetzelfde); bij meer wordt er tussen de
 * stappen gemengd.
 *
 * @param {string[]} ramp
 * @param {number} count
 * @returns {string[]}
 */
const sampleRamp = (ramp, count) => {
    if (!Number.isFinite(count) || count <= 0) return []
    if (count === 1) return [ramp[Math.floor(ramp.length / 2)]]

    return Array.from({ length: Math.floor(count) }, (_, i) => {
        const positie = (i / (Math.floor(count) - 1)) * (ramp.length - 1)
        const onder = Math.floor(positie)
        const rest = positie - onder
        if (rest === 0) return ramp[onder]
        return meng(ramp[onder], ramp[onder + 1], rest)
    })
}

/**
 * `count` kleuren voor een ordinale reeks (Likert, participatieladder,
 * schaalverdeling). Eén niveau krijgt de merk-teal.
 *
 * @param {number} count
 * @returns {string[]}
 */
export const getOrdinalColors = (count) => sampleRamp(SEQUENTIAL, count)

/**
 * `count` kleuren voor een divergerende reeks. Bij een oneven aantal valt het
 * neutrale midden precies op het middelste niveau.
 *
 * @param {number} count
 * @returns {string[]}
 */
export const getDivergingColors = (count) => sampleRamp(DIVERGING, count)

/** De donkere tekstkleur van de app (--ink-strong in tokens.css). */
const LABEL_INK = '#1a2428'

/**
 * Welke tekstkleur een getal in een gekleurd vlak (taartpunt, staaf) moet
 * krijgen: licht of donker, wat het meeste contrast geeft. Donkere tekst op
 * donker teal was slecht leesbaar (testronde 2026-10-05).
 *
 * @param {string} [fill] - Kleur van het vlak, als hexcode
 * @returns {'light'|'dark'}
 */
export const labelTextTone = (fill) => {
    if (typeof fill !== 'string' || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(fill)) return 'dark'
    return contrastRatio(fill, '#ffffff') > contrastRatio(fill, LABEL_INK) ? 'light' : 'dark'
}

